import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const SESSION_ENDPOINT = `${import.meta.env.BASE_URL}api/admin/session`;

interface AdminAuthCtx {
  authHeader: string | null;
  login: (user: string, pass: string) => Promise<boolean>;
  logout: () => void;
}

const Ctx = createContext<AdminAuthCtx | null>(null);

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [authHeader, setAuthHeader] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    fetch(SESSION_ENDPOINT, { credentials: "same-origin" })
      .then((res) => {
        if (res.ok) setAuthHeader("cookie");
      })
      .catch(() => {/* network error — stay unauthenticated */})
      .finally(() => setChecked(true));
  }, []);

  const login = useCallback(async (user: string, pass: string): Promise<boolean> => {
    const encoded = btoa(`${user}:${pass}`);
    try {
      const res = await fetch(SESSION_ENDPOINT, {
        method: "POST",
        credentials: "same-origin",
        headers: { Authorization: `Basic ${encoded}` },
      });
      if (res.ok) {
        setAuthHeader("cookie");
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(SESSION_ENDPOINT, { method: "DELETE", credentials: "same-origin" });
    } catch {
      // ignore
    }
    setAuthHeader(null);
  }, []);

  if (!checked) return null;

  return <Ctx.Provider value={{ authHeader, login, logout }}>{children}</Ctx.Provider>;
}

export function useAdminAuth(): AdminAuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
